import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  MapPin, Calendar, ArrowRight, Truck, Trophy, Bell, DollarSign, Check,
  Smartphone, Route, Users, Globe, Fuel, Clock, ShieldCheck, Menu, X,
  Crown, PhoneCall, Loader2,
} from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import PricingModel from "@/components/PricingModel";
import api from "@/lib/api";

const RED = "#C8000F";

const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y: 28 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.55, delay, ease: [0.22, 1, 0.36, 1] }}
    className={className}
  >
    {children}
  </motion.div>
);

const STATS = [
  { value: "474+", label: "Rodeos indexed" },
  { value: "2", label: "Countries (CA · US)" },
  { value: "1", label: "App for your whole season" },
  { value: "100%", label: "Built for contestants" },
];

const STEPS = [
  { n: "01", icon: Calendar, title: "Pick your rodeos", desc: "Browse 474+ rodeos with entry open/close dates, official links and one-tap calendar sync." },
  { n: "02", icon: Route, title: "Optimize your haul", desc: "Map your A→B→C route, see mileage, drive time and fuel cost, then hand off to Google or Apple Maps." },
  { n: "03", icon: Trophy, title: "Enter, trade & win", desc: "Track draws, post trade requests, watch deadlines and follow season standings — all in one place." },
];

const FEATURES = [
  { icon: Bell, title: "Smart Calendar & Deadline Alerts", desc: "Never miss an entry window. Push reminders before entries open and close for every rodeo you follow." },
  { icon: Fuel, title: "A→B→C Trip Planner & Fuel Estimator", desc: "Exact mileage, drive time and rig fuel cost across your route — with auto route optimization and maps handoff." },
  { icon: MapPin, title: "Synced Map · Table · Calendar", desc: "Search any rodeo and it lights up everywhere at once — the map flies to it while the table and calendar follow." },
  { icon: Users, title: "Follow Your Riders", desc: "Track any contestant's whole season — every rodeo they've entered, where they're up next, and every placing, time and cheque." },
  { icon: Truck, title: "Draw Sheets & Trade Board", desc: "Check who's up, find partners to trade performances with, and lock your positions before you nod." },
  { icon: DollarSign, title: "Rodeo Credits — Pay As You Go", desc: "One simple currency for prepared entries, draw lookups and trade reports. Top up anytime and only pay for what you actually use." },
];

// White-glove concierge tier — a real Rodeo Pilot cowboy runs your season. Shown as a
// standalone premium banner under the grid; CTA opens a lead-capture form (Stripe not wired).
const FULL_SERVICE = {
  name: "Full Service",
  badge: "White-glove concierge",
  monthly: 500, yearly: 5000,
  tagline: "Hang up your hat on the paperwork — we'll run your season.",
  features: [
    "A certified, registered Rodeo Pilot cowboy builds your full season plan for you",
    "We enter & manage your rodeos, deadlines and trades on your behalf",
    "Regular check-ins so you're always in the loop",
    "Everything in Team & Family included",
  ],
};

const FAQ = [
  { q: "Is there a free version?", a: "Yes — the Rookie plan is free forever: browse all 474+ rodeos, view the calendar and season standings, and follow a competitor. You get welcome credits to try prepared entries and draw lookups, and you only pay for what you use." },
  { q: "How does pricing work now?", a: "Rookie is free. Semi Pro is $14.99/mo ($149/yr) to run your whole season, and Professional is $29.99/mo ($299/yr) for the full performance platform with AI Coach. Spectator plans start at $2.99/mo for fans who just want to follow riders — and come with a 7-day free trial, no credit card required. All prices in CAD." },
  { q: "What are Rodeo Credits?", a: "Credits are one simple currency across Rodeo (target value: 1 credit ≈ $1). You spend them on things like prepared entries, draw lookups, trade reports and results audits — and you can top up anytime from your Wallet without changing your plan." },
  { q: "What is the Full Service plan?", a: "It's our white-glove concierge tier at $500/mo. A certified Rodeo Pilot cowboy builds your full season plan, enters and manages your rodeos, deadlines and trades on your behalf, and keeps you in the loop with regular check-ins. Tap \"Request a call\" and we'll match you with your planner." },
  { q: "Does Rodeo Pilot work on my phone?", a: "Yes. Rodeo Pilot runs in any web browser on your phone, tablet and desktop, and your season syncs across all of them. Add it to your home screen for one-tap, app-like access." },
  { q: "Which associations are in the directory?", a: "474+ rodeos across CPRA, PRCA and major sanctioning bodies in Canada and the US — each linked to its official website." },
  { q: "Can I export entry dates to my phone calendar?", a: "One tap generates an .ics file that syncs with Apple Calendar, Google Calendar and Outlook." },
];

const grain = {
  backgroundImage:
    "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='140' height='140'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='3'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.55'/%3E%3C/svg%3E\")",
};

export default function Landing() {
  const nav = useNavigate();
  const go = () => nav("/login?signup=1");
  const goLogin = () => nav("/login");
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [applyOpen, setApplyOpen] = useState(false);
  const [lead, setLead] = useState({ name: "", phone: "", email: "", events: "", note: "" });
  const [applyState, setApplyState] = useState("idle"); // idle | sending | done | error
  const submitLead = async (e) => {
    e.preventDefault();
    if (!lead.name.trim() || !lead.phone.trim()) { setApplyState("error"); return; }
    setApplyState("sending");
    try {
      await api.post("/leads/full-service", lead);
      setApplyState("done");
    } catch { setApplyState("error"); }
  };
  const openApply = () => { setLead({ name: "", phone: "", email: "", events: "", note: "" }); setApplyState("idle"); setApplyOpen(true); };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const NAV = [["Features", "features"], ["Directory", "directory"], ["Trip Planner", "how"], ["Pricing", "pricing"], ["FAQ", "faq"]];
  const scrollTo = (id) => { setMenuOpen(false); document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }); };

  return (
    <div className="min-h-screen bg-[#0C1017] text-slate-100 overflow-x-hidden" style={{ fontFamily: "Inter, sans-serif" }}>
      {/* grain overlay */}
      <div className="pointer-events-none fixed inset-0 z-0 opacity-[0.035] mix-blend-screen" style={grain} />

      {/* Header */}
      <header className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${scrolled ? "bg-[#0C1017]/85 backdrop-blur-md border-b border-white/10" : "bg-transparent"}`}>
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <button onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} data-testid="landing-logo" className="flex items-center">
            <img src="/rodeo-pilot-wordmark.webp" alt="Rodeo Pilot" className="h-11 sm:h-14 w-auto object-contain" />
          </button>
          <nav className="hidden lg:flex items-center gap-8">
            {NAV.map(([label, id]) => (
              <button key={id} onClick={() => scrollTo(id)} data-testid={`nav-${id}`} className="text-sm text-slate-300 hover:text-white transition-colors">{label}</button>
            ))}
          </nav>
          <div className="hidden lg:flex items-center gap-3">
            <button onClick={goLogin} data-testid="header-login-btn" className="text-sm font-medium text-slate-200 hover:text-white px-3 py-2">Log in</button>
            <button onClick={go} data-testid="header-cta-btn" className="text-sm font-bold text-white px-4 py-2.5 rounded-lg transition-transform hover:scale-[1.03]" style={{ background: RED }}>Get Started Free</button>
          </div>
          <button onClick={() => setMenuOpen((o) => !o)} data-testid="mobile-menu-btn" className="lg:hidden text-slate-200 p-2">{menuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}</button>
        </div>
        {menuOpen && (
          <div className="lg:hidden bg-[#0C1017] border-b border-white/10 px-5 py-4 space-y-1">
            {NAV.map(([label, id]) => <button key={id} onClick={() => scrollTo(id)} className="block w-full text-left py-2.5 text-slate-300">{label}</button>)}
            <button onClick={go} className="w-full mt-2 text-white font-bold py-3 rounded-lg" style={{ background: RED }}>Get Started Free</button>
          </div>
        )}
      </header>

      {/* Hero */}
      <section className="relative z-10 pt-28 pb-20 sm:pt-36 sm:pb-28">
        <div className="absolute inset-0 z-0">
          <img src="https://images.unsplash.com/photo-1723581205436-dd047a4def30?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920" alt="" className="w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0C1017]/70 via-[#0C1017]/85 to-[#0C1017]" />
        </div>
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <motion.span initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
              className="inline-flex items-center gap-2 text-xs uppercase tracking-widest font-semibold px-3 py-1.5 rounded-full border border-white/15 text-slate-300 mb-6">
              <MapPin className="w-3.5 h-3.5" style={{ color: RED }} /> Built for the haul · CA · US
            </motion.span>
            <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.05 }}
              className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05]">
              Plan your whole <span style={{ color: RED }}>rodeo season</span> in one place.
            </motion.h1>
            <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.15 }}
              className="mt-6 text-lg sm:text-xl text-slate-300 leading-relaxed max-w-xl">
              The trip planner, entry-deadline radar, draw tracker and rodeo directory built for western contestants — so you enter more, drive smarter and never miss a nod.
            </motion.p>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.25 }}
              className="mt-8 flex flex-col sm:flex-row gap-3">
              <button onClick={go} data-testid="hero-cta-button" className="inline-flex items-center justify-center gap-2 text-white font-bold px-7 py-3.5 rounded-xl transition-transform hover:scale-[1.03]" style={{ background: RED }}>
                Get Started Free <ArrowRight className="w-4 h-4" />
              </button>
              <button onClick={() => scrollTo("directory")} data-testid="hero-secondary-button" className="inline-flex items-center justify-center gap-2 font-semibold px-7 py-3.5 rounded-xl border border-white/15 text-slate-200 hover:bg-white/5 transition-colors">
                Explore 474+ Rodeos
              </button>
            </motion.div>
            <div className="mt-6 flex items-center gap-2 text-sm text-slate-400">
              <Smartphone className="w-4 h-4" /> Free on any device · no card required
            </div>
          </div>

          {/* hero mock card */}
          <motion.div initial={{ opacity: 0, scale: 0.94 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7, delay: 0.2 }} className="lg:col-span-5">
            <div className="rounded-2xl border border-white/10 bg-[#141A24]/90 backdrop-blur p-5 shadow-2xl">
              <div className="flex items-center justify-between mb-3">
                <span className="flex items-center gap-2 text-sm font-bold"><Route className="w-4 h-4" style={{ color: RED }} /> Trip Planner</span>
                <span className="text-xs text-slate-400">3 stops</span>
              </div>
              {[["Ponoka Stampede", "Ponoka, AB", true], ["Wainwright Stampede", "Wainwright, AB", false], ["Medicine Hat Stampede", "Medicine Hat, AB", false]].map(([n, c], i) => (
                <div key={i} className="flex items-center gap-3 py-2 border-b border-white/5 last:border-0">
                  <span className="w-6 h-6 rounded-full grid place-items-center text-white text-xs font-bold" style={{ background: i === 0 ? "#5FA83B" : RED }}>{i + 1}</span>
                  <div className="flex-1 min-w-0"><div className="text-sm font-semibold truncate">{n}</div><div className="text-xs text-slate-400">{c}</div></div>
                </div>
              ))}
              <div className="mt-3 grid grid-cols-3 gap-2 text-center">
                {[[MapPin, "612 km"], [Clock, "~6.4 h"], [Fuel, "~$132"]].map(([Ic, v], i) => (
                  <div key={i} className="rounded-lg bg-white/5 py-2"><Ic className="w-4 h-4 mx-auto mb-1 text-slate-400" /><div className="text-sm font-bold">{v}</div></div>
                ))}
              </div>
              <div className="mt-3 flex items-center gap-2 rounded-lg bg-[#12233B] border border-[#1E3A5F] px-3 py-2 text-xs text-[#9CC4EE]">
                <Bell className="w-3.5 h-3.5" /> Ponoka entries close in 2 days
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Trust strip */}
      <section className="relative z-10 border-y border-white/10 bg-[#0F1520]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-8">
          <p className="text-center text-xs uppercase tracking-widest text-slate-500 mb-6">Covering contestants across</p>
          <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-3 mb-8">
            {["CPRA", "PRCA", "High School & College"].map((a) => (
              <span key={a} className="font-display font-bold text-slate-400 tracking-wide">{a}</span>
            ))}
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {STATS.map((s) => (
              <div key={s.label} className="text-center">
                <div className="font-display text-3xl sm:text-4xl font-extrabold" style={{ color: RED }}>{s.value}</div>
                <div className="text-sm text-slate-400 mt-1">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="how" className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 py-20 sm:py-28">
        <Reveal className="text-center max-w-2xl mx-auto">
          <div className="text-xs uppercase tracking-widest font-semibold mb-3" style={{ color: RED }}>How it works</div>
          <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">Built for the haul. Built for the win.</h2>
        </Reveal>
        <div className="grid md:grid-cols-3 gap-6 mt-14">
          {STEPS.map((s, i) => (
            <Reveal key={s.n} delay={i * 0.1}>
              <div className="relative rounded-2xl border border-white/10 bg-[#141A24] p-7 h-full">
                <span className="font-display text-5xl font-extrabold text-white/10 absolute top-4 right-5">{s.n}</span>
                <span className="w-12 h-12 rounded-xl grid place-items-center mb-5" style={{ background: "rgba(200, 0, 15,0.12)" }}><s.icon className="w-6 h-6" style={{ color: RED }} /></span>
                <h3 className="font-display text-xl font-semibold mb-2">{s.title}</h3>
                <p className="text-slate-400 leading-relaxed">{s.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Features */}
      <section id="features" className="relative z-10 bg-[#0F1520] border-y border-white/10">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20 sm:py-28">
          <Reveal className="max-w-2xl">
            <div className="text-xs uppercase tracking-widest font-semibold mb-3" style={{ color: RED }}>Everything in one app</div>
            <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">Stop juggling texts, spreadsheets and phone calls.</h2>
          </Reveal>
          <div className="grid md:grid-cols-2 gap-6 mt-14">
            {FEATURES.map((f, i) => (
              <Reveal key={f.title} delay={(i % 2) * 0.1}>
                <div className="flex gap-4 rounded-2xl border border-white/10 bg-[#141A24] p-6 h-full hover:border-[rgba(200, 0, 15,0.4)] transition-colors">
                  <span className="shrink-0 w-12 h-12 rounded-xl grid place-items-center" style={{ background: "rgba(200, 0, 15,0.12)" }}><f.icon className="w-6 h-6" style={{ color: RED }} /></span>
                  <div><h3 className="font-display text-lg font-semibold mb-1.5">{f.title}</h3><p className="text-slate-400 leading-relaxed text-sm">{f.desc}</p></div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Directory band */}
      <section id="directory" className="relative z-10 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img src="https://images.unsplash.com/photo-1783792830593-a2742453f9d3?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920" alt="" className="w-full h-full object-cover opacity-20" />
          <div className="absolute inset-0 bg-[#0C1017]/85" />
        </div>
        <div className="relative max-w-5xl mx-auto px-5 sm:px-8 py-20 sm:py-28 text-center">
          <Reveal>
            <Globe className="w-10 h-10 mx-auto mb-5" style={{ color: RED }} />
            <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">Every rodeo, one directory.</h2>
            <p className="mt-4 text-lg text-slate-300 max-w-2xl mx-auto">474+ rodeos across Canada and the US — searchable by country and province, each linked straight to the official source.</p>
            <button onClick={go} data-testid="directory-cta-button" className="mt-8 inline-flex items-center gap-2 text-white font-bold px-7 py-3.5 rounded-xl transition-transform hover:scale-[1.03]" style={{ background: RED }}>Browse the Directory <ArrowRight className="w-4 h-4" /></button>
          </Reveal>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="relative z-10 pt-20 sm:pt-28">
        <Reveal className="text-center max-w-2xl mx-auto px-5 sm:px-8">
          <div className="text-xs uppercase tracking-widest font-semibold mb-3" style={{ color: RED }}>Pricing</div>
          <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">Choose how you rodeo.</h2>
          <p className="mt-4 text-slate-400">Follow riders, plan your season, manage your entries and improve your performance — one platform, with credits you spend only when you need them. All prices in CAD.</p>
        </Reveal>

        <PricingModel onCta={go} />

        {/* Full Service — white-glove concierge banner */}
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <Reveal className="mt-8">
          <div data-testid="pricing-tier-full-service"
            className="relative overflow-hidden rounded-2xl border border-[#E6B35A]/40 bg-gradient-to-br from-[#1A1205] via-[#141A24] to-[#160A0B] p-7 sm:p-9">
            <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full blur-3xl" style={{ background: "rgba(230,179,90,0.12)" }} />
            <div className="relative flex flex-col lg:flex-row lg:items-center gap-7">
              <div className="flex-1 min-w-0">
                <div className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide px-3 py-1 rounded-full text-[#1A1205]" style={{ background: "#E6B35A" }}>
                  <Crown className="w-3.5 h-3.5" /> {FULL_SERVICE.badge}
                </div>
                <h3 className="font-display text-3xl font-extrabold mt-3">{FULL_SERVICE.name}</h3>
                <p className="text-slate-300 mt-1.5 max-w-xl">{FULL_SERVICE.tagline}</p>
                <ul className="mt-5 grid sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
                  {FULL_SERVICE.features.map((f) => (
                    <li key={f} className="flex gap-2.5 text-slate-200">
                      <Check className="w-4 h-4 shrink-0 mt-0.5" style={{ color: "#E6B35A" }} />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="lg:w-64 shrink-0 lg:border-l lg:border-white/10 lg:pl-7">
                <div className="flex items-end gap-1">
                  <span className="font-display text-4xl font-extrabold">${FULL_SERVICE.monthly}</span>
                  <span className="text-slate-400 text-sm mb-1.5">/month</span>
                </div>
                <p className="text-xs text-slate-500 mt-1">or ${FULL_SERVICE.yearly.toLocaleString()}/year · CAD</p>
                <button onClick={openApply} data-testid="pricing-cta-full-service"
                  className="mt-4 w-full inline-flex items-center justify-center gap-2 font-bold py-3 rounded-xl text-[#1A1205] transition-transform hover:scale-[1.02]"
                  style={{ background: "#E6B35A" }}>
                  <PhoneCall className="w-4 h-4" /> Request a call
                </button>
                <p className="text-[11px] text-slate-500 mt-2 text-center">Limited spots · we'll match you with a planner</p>
              </div>
            </div>
          </div>
        </Reveal>
        </div>
      </section>

      {/* Full Service lead-capture modal */}
      {applyOpen && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4" data-testid="full-service-modal">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setApplyOpen(false)} />
          <div className="relative w-full max-w-md rounded-2xl border border-[#E6B35A]/40 bg-[#141A24] p-6 shadow-2xl">
            <button onClick={() => setApplyOpen(false)} data-testid="full-service-close" className="absolute top-4 right-4 text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
            {applyState === "done" ? (
              <div className="text-center py-6" data-testid="full-service-success">
                <div className="mx-auto w-12 h-12 rounded-full flex items-center justify-center" style={{ background: "rgba(230,179,90,0.15)" }}><Check className="w-6 h-6" style={{ color: "#E6B35A" }} /></div>
                <h3 className="font-display text-2xl font-bold mt-4">You're on the list!</h3>
                <p className="text-slate-400 mt-2 text-sm">A certified Rodeo Pilot cowboy will reach out to build your season plan. Keep an eye on your phone.</p>
                <button onClick={() => setApplyOpen(false)} className="mt-6 w-full font-bold py-3 rounded-xl text-white" style={{ background: RED }}>Done</button>
              </div>
            ) : (
              <>
                <div className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide px-3 py-1 rounded-full text-[#1A1205]" style={{ background: "#E6B35A" }}><Crown className="w-3.5 h-3.5" /> Full Service</div>
                <h3 className="font-display text-2xl font-bold mt-3">Get matched with your planner</h3>
                <p className="text-slate-400 mt-1 text-sm">Leave your details and a certified Rodeo Pilot cowboy will call to set up your season.</p>
                <form onSubmit={submitLead} className="mt-5 space-y-3">
                  <input data-testid="fs-name" value={lead.name} onChange={(e) => setLead({ ...lead, name: e.target.value })} placeholder="Your name *"
                    className="w-full bg-[#0F1520] border border-white/10 rounded-xl px-4 py-3 text-[16px] text-white placeholder-slate-500 focus:border-[#E6B35A] outline-none" />
                  <input data-testid="fs-phone" value={lead.phone} onChange={(e) => setLead({ ...lead, phone: e.target.value })} placeholder="Phone number *" inputMode="tel"
                    className="w-full bg-[#0F1520] border border-white/10 rounded-xl px-4 py-3 text-[16px] text-white placeholder-slate-500 focus:border-[#E6B35A] outline-none" />
                  <input data-testid="fs-email" value={lead.email} onChange={(e) => setLead({ ...lead, email: e.target.value })} placeholder="Email (optional)" inputMode="email"
                    className="w-full bg-[#0F1520] border border-white/10 rounded-xl px-4 py-3 text-[16px] text-white placeholder-slate-500 focus:border-[#E6B35A] outline-none" />
                  <input data-testid="fs-events" value={lead.events} onChange={(e) => setLead({ ...lead, events: e.target.value })} placeholder="Your events (e.g. Bull Riding, Team Roping)"
                    className="w-full bg-[#0F1520] border border-white/10 rounded-xl px-4 py-3 text-[16px] text-white placeholder-slate-500 focus:border-[#E6B35A] outline-none" />
                  <textarea data-testid="fs-note" value={lead.note} onChange={(e) => setLead({ ...lead, note: e.target.value })} placeholder="Anything we should know? (optional)" rows={2}
                    className="w-full bg-[#0F1520] border border-white/10 rounded-xl px-4 py-3 text-[16px] text-white placeholder-slate-500 focus:border-[#E6B35A] outline-none resize-none" />
                  {applyState === "error" && <p className="text-sm text-[#F26D77]" data-testid="fs-error">Please enter at least your name and phone number.</p>}
                  <button type="submit" disabled={applyState === "sending"} data-testid="fs-submit"
                    className="w-full inline-flex items-center justify-center gap-2 font-bold py-3 rounded-xl text-[#1A1205] disabled:opacity-60" style={{ background: "#E6B35A" }}>
                    {applyState === "sending" ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</> : <><PhoneCall className="w-4 h-4" /> Request a call</>}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      )}

      {/* FAQ */}
      <section id="faq" className="relative z-10 bg-[#0F1520] border-y border-white/10">
        <div className="max-w-3xl mx-auto px-5 sm:px-8 py-20 sm:py-28">
          <Reveal className="text-center mb-10">
            <div className="text-xs uppercase tracking-widest font-semibold mb-3" style={{ color: RED }}>FAQ</div>
            <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">Questions, answered.</h2>
          </Reveal>
          <Reveal>
            <Accordion type="single" collapsible className="space-y-3">
              {FAQ.map((item, i) => (
                <AccordionItem key={i} value={`q${i}`} data-testid={`faq-item-${i}`} className="border border-white/10 rounded-xl bg-[#141A24] px-5">
                  <AccordionTrigger className="text-left font-semibold hover:no-underline">{item.q}</AccordionTrigger>
                  <AccordionContent className="text-slate-400 leading-relaxed">{item.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>

      {/* Final CTA */}
      <section className="relative z-10 max-w-5xl mx-auto px-5 sm:px-8 py-20 sm:py-28">
        <Reveal>
          <div className="relative overflow-hidden rounded-3xl border border-white/10 p-10 sm:p-14 text-center" style={{ background: "linear-gradient(135deg, #1A1010 0%, #0C1017 60%)" }}>
            <div className="absolute inset-0 opacity-[0.06]" style={grain} />
            <div className="relative">
              <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">Ready to master your rodeo season?</h2>
              <p className="mt-4 text-lg text-slate-300 max-w-xl mx-auto">Save fuel, hit every entry deadline, and take the stress out of hauling. Start free — no card required.</p>
              <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
                <button onClick={go} data-testid="final-cta-button" className="inline-flex items-center justify-center gap-2 text-white font-bold px-8 py-4 rounded-xl transition-transform hover:scale-[1.03]" style={{ background: RED }}>Get Started Free <ArrowRight className="w-4 h-4" /></button>
                <span className="inline-flex items-center justify-center gap-2 text-slate-300 text-sm px-4"><Smartphone className="w-4 h-4" /> Works on any device</span>
              </div>
            </div>
          </div>
        </Reveal>
      </section>

      {/* Footer */}
      <footer className="relative z-10 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-12 grid md:grid-cols-4 gap-8">
          <div className="md:col-span-1">
            <div className="mb-3">
              <img src="/rodeo-pilot-wordmark.webp" alt="Rodeo Pilot" className="h-11 w-auto object-contain" />
            </div>
            <p className="text-sm text-slate-500 leading-relaxed">Season planning & trip optimization built for rodeo athletes.</p>
          </div>
          {[
            ["Product", [["Trip Planner", () => scrollTo("how")], ["Calendar & Deadlines", () => scrollTo("features")], ["Trade Board", () => scrollTo("features")], ["Directory", () => scrollTo("directory")], ["Pricing", () => scrollTo("pricing")]]],
            ["Associations", [["CPRA Canada", () => window.open("https://rodeocanada.com", "_blank", "noopener")], ["PRCA USA", () => window.open("https://www.prorodeo.com", "_blank", "noopener")]]],
            ["Company", [["Privacy Policy", () => nav("/privacy")], ["Terms of Service", () => nav("/terms")], ["Contact", () => nav("/contact")]]],
          ].map(([title, items]) => (
            <div key={title}>
              <div className="font-display font-bold text-sm mb-3">{title}</div>
              <ul className="space-y-2">{items.map(([label, action]) => <li key={label}><button onClick={action} data-testid={`footer-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="text-sm text-slate-400 hover:text-white transition-colors">{label}</button></li>)}</ul>
            </div>
          ))}
        </div>
        <div className="border-t border-white/10 py-5 text-center text-xs text-slate-500">© 2026 Rodeo Pilot. All rights reserved.</div>
      </footer>
    </div>
  );
}
